import { CloseCircleOutlined, InboxOutlined } from "@ant-design/icons";
import { Form, Upload } from "antd";
import React, { useCallback, useEffect } from "react";

const { Dragger } = Upload;

type Props = {
    imageUrl?: string | null;
    canvasRef: React.RefObject<HTMLCanvasElement>;
    fileList: any[];
    handleFileChange: (info: any) => void;
    handleRemove: () => void;
    CypressTestIds: Record<string, string>;
    previewSize?: number;
};

export const CorporateLogoUpload: React.FC<Props> = ({ imageUrl, canvasRef, fileList, handleFileChange, handleRemove, CypressTestIds, previewSize = 1024 }) => {
    const drawCheckerboard = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number, tile = 100) => {
        const c1 = "#f2f2f2";
        const c2 = "#d9d9d9";

        for (let y = 0; y < height; y += tile) {
            for (let x = 0; x < width; x += tile) {
                const isAlt = (x / tile + y / tile) % 2 === 0;
                ctx.fillStyle = isAlt ? c1 : c2;
                ctx.fillRect(x, y, tile, tile);
            }
        }
    }, []);

    const drawImageContained = useCallback((ctx: CanvasRenderingContext2D, img: HTMLImageElement, canvasW: number, canvasH: number) => {
        const iw = img.naturalWidth || img.width;
        const ih = img.naturalHeight || img.height;

        const scale = Math.min(canvasW / iw, canvasH / ih);
        const dw = Math.floor(iw * scale);
        const dh = Math.floor(ih * scale);
        const dx = Math.floor((canvasW - dw) / 2);
        const dy = Math.floor((canvasH - dh) / 2);

        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.18)";
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2;

        ctx.drawImage(img, dx, dy, dw, dh);
        ctx.restore();

        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.10)";
        ctx.lineWidth = Math.max(2, Math.floor(canvasW / 512));
        ctx.strokeRect(dx + 1, dy + 1, dw - 2, dh - 2);
        ctx.restore();
    }, []);

    const renderPreview = useCallback(async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d", { alpha: true });
        if (!ctx) return;

        canvas.width = previewSize;
        canvas.height = previewSize;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawCheckerboard(ctx, canvas.width, canvas.height, Math.max(16, Math.floor(previewSize / 24)));

        if (!imageUrl) return;

        await new Promise<void>((resolve, reject) => {
            const img = new Image();

            img.onload = () => {
                drawImageContained(ctx, img, canvas.width, canvas.height);
                resolve();
            };
            img.onerror = () => reject(new Error("Failed to load logo image for preview."));
            img.src = imageUrl;
        });
    }, [canvasRef, imageUrl, previewSize, drawCheckerboard, drawImageContained]);

    useEffect(() => {
        renderPreview().catch(() => {});
    }, [renderPreview]);

    return (
        <Form.Item label="Corporate Logo" name="logo" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} className="flex gap-4">
            <div className="flex gap-4">
                <div className="relative flex h-[160px] w-[160px] items-center justify-center overflow-hidden rounded-md border-2 border-dashed">
                    {imageUrl ? (
                        <>
                            <canvas ref={canvasRef} id="logo-preview" data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_IMAGE_PREVIEW} className="block h-full w-full" />
                            <CloseCircleOutlined
                                className="absolute right-2 top-2 cursor-pointer text-lg text-red-500"
                                data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_DELETE_IMAGE_BUTTON}
                                onClick={handleRemove}
                            />
                        </>
                    ) : (
                        <Dragger
                            accept=".jpg,.jpeg,.png"
                            multiple={false}
                            beforeUpload={() => false}
                            onChange={handleFileChange}
                            fileList={fileList}
                            showUploadList={false}
                            className="flex h-full w-full items-center justify-center"
                        >
                            <div className="text-center" data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_IMAGE_INPUT}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                            </div>
                        </Dragger>
                    )}
                </div>

                <div>
                    <p className="text-sm">Accepted file type: JPG, PNG</p>
                    <p className="text-sm">(200x200 pixels - 1024x1024 pixels)</p>
                </div>
            </div>
        </Form.Item>
    );
};
